import React, { useState, useEffect } from "react";
type Props = {
  targetAudioId: string; // ID de l'<audio> à contrôler
  label?: string; // défaut: "Écouter l'essentiel : 5 min"
  className?: string;
};
export default function HeroPlayButton({
  targetAudioId,
  label = "Écouter l'essentiel : 5 min",
  className = ""
}: Props) {
  const [isPlaying, setIsPlaying] = useState(false);
  const onClick = () => {
    const el = document.getElementById(targetAudioId) as HTMLAudioElement | null;
    if (!el) return;
    if (isPlaying) {
      el.pause();
    } else {
      // Lancer la lecture DANS la même interaction utilisateur
      el.play().catch(() => {/* si refus, l'utilisateur voit le lecteur */});
      // Puis défiler (et focus pour accessibilité)  
      el.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      try {
        el.focus({
          preventScroll: true
        });
      } catch {}
    }
  };

  // Écouter les événements audio pour mettre à jour l'état
  useEffect(() => {
    const el = document.getElementById(targetAudioId) as HTMLAudioElement | null;
    if (!el) return;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);
    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
    };
  }, [targetAudioId]);
  return <button type="button" onClick={onClick} role="button" aria-label={isPlaying ? "Mettre en pause" : label} aria-controls={targetAudioId} className={["relative group transition-all duration-300 flex items-center justify-center", "hover:scale-105 focus:outline-none focus:scale-105", className].join(" ")} style={{
    WebkitTapHighlightColor: "transparent"
  }}>
      {/* Animation d'attraction intensifiée avant lecture pour inciter au clic */}
      {!isPlaying}

      {/* Ondes radiatives scintillantes pendant la lecture */}
      {isPlaying && <div className="absolute inset-0 flex items-center justify-center">
          {/* Cercles radiatifs de l'intérieur vers l'extérieur */}
          <div className="absolute w-24 h-24 rounded-full border-2 border-orange-400/50" style={{
        animation: 'radiation-wave-1 1.2s ease-out infinite'
      }} />
          <div className="absolute w-32 h-32 rounded-full border-2 border-orange-400/40" style={{
        animation: 'radiation-wave-2 1.6s ease-out infinite',
        animationDelay: '0.3s'
      }} />
          <div className="absolute w-40 h-40 rounded-full border border-orange-400/30" style={{
        animation: 'radiation-wave-3 2s ease-out infinite',
        animationDelay: '0.6s'
      }} />
          <div className="absolute w-48 h-48 rounded-full border border-orange-400/20" style={{
        animation: 'radiation-wave-4 2.4s ease-out infinite',
        animationDelay: '0.9s'
      }} />
          
          {/* Particules scintillantes amplifiées pendant lecture */}
          <div className="absolute top-2 left-4 w-1 h-1 bg-orange-300 rounded-full" style={{
        animation: 'sparkle-intense-1 1s ease-in-out infinite'
      }} />
          <div className="absolute bottom-4 right-2 w-1 h-1 bg-orange-400 rounded-full" style={{
        animation: 'sparkle-intense-2 1.2s ease-in-out infinite'
      }} />
          <div className="absolute top-6 right-3 w-1 h-1 bg-orange-200 rounded-full" style={{
        animation: 'sparkle-intense-3 1.4s ease-in-out infinite'
      }} />
          <div className="absolute bottom-2 left-6 w-0.5 h-0.5 bg-orange-500 rounded-full" style={{
        animation: 'sparkle-intense-1 1.1s ease-in-out infinite',
        animationDelay: '0.3s'
      }} />
          <div className="absolute top-8 left-2 w-0.5 h-0.5 bg-orange-300 rounded-full" style={{
        animation: 'sparkle-intense-2 1.3s ease-in-out infinite',
        animationDelay: '0.6s'
      }} />
        </div>}
      
      {/* Bouton minimaliste agrandi */}
      
      
      {/* Bulle agrandie en blanc repositionnée plus bas en deux lignes */}
      <div className="absolute top-20 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 ease-out z-20">
        <div className="bg-transparent text-white text-sm font-medium px-2 py-1 rounded whitespace-nowrap text-center">
          <div>Notre approche</div>
          <div>en 5min</div>
        </div>
      </div>
    </button>;
}